import _ from 'lodash';
import {
  sqCapsuleRuntimeStore,
  sqDurationStore,
  sqInvestigateStore,
  sqTrendCapsuleStore,
  sqTrendStore,
} from '@/core/core.stores';
import { nanosToMillis } from '@/datetime/dateTime.utilities';
import { mergeCapsuleSelection, splitUniqueId } from '@/trendData/trendCapsule.utilities';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { getCertainId } from '@/utilities/id.utilities';
import type { CapsuleItem, PreviewChartItem, SelectableCapsule, SelectedCapsule } from '@/trendData/trendData.types';

/**
 * Read-side selectors for the capsule dataset. The runtime store owns the data; the flux store owns
 * selection and editing state; these selectors merge the two so no store reads the other.
 */

/** The capsule items with the flux-owned selection merged in */
export function getCapsuleItems(): CapsuleItem[] {
  return mergeCapsuleSelection(sqCapsuleRuntimeStore.getState().items, sqTrendCapsuleStore.selectedCapsules);
}

/**
 * Resolves a capsule to its capsule dataset entry when the chart and the table are out of sync, by
 * matching a start or end time within the same condition. In capsule picking mode the selection is
 * the source of truth rather than the dataset, so the capsule is returned as is; otherwise removing a
 * capsule from the picked capsules via the [x] buttons in the capsule group component would resolve
 * it onto a table capsule with the same times from the same condition, which happens easily when
 * picking from a condition derived from the manual condition being edited. The trade-off is that
 * selecting an in-progress capsule on the chart while picking may not select it in the capsules pane.
 */
export function resolveCapsuleItem(item: SelectedCapsule): SelectedCapsule {
  const items = sqCapsuleRuntimeStore.getState().items;
  if (sqInvestigateStore.isCapsulePickingMode || items.some((candidate) => candidate.id === item.id)) {
    return item;
  }
  const conditionId = splitUniqueId(item.id).conditionId;

  return (
    items.find(
      (capsule) =>
        capsule.isChildOf === conditionId && (item.startTime === capsule.startTime || item.endTime === capsule.endTime),
    ) ?? item
  );
}

/**
 * Finds a capsule item by id with selection merged in. While a condition is being edited, its preview
 * lane (or one of the preview lanes) stands in for an id that is not in the dataset.
 */
export function findCapsuleItem(id: string): CapsuleItem | undefined {
  const { items, previewChartItem } = sqCapsuleRuntimeStore.getState();
  const item = items.find((candidate) => candidate.id === id);
  if (item) {
    return { ...item, selected: sqTrendCapsuleStore.isSelected(item.id) };
  }
  if (Array.isArray(previewChartItem)) {
    return previewChartItem.find((lane) => lane.id === id);
  }

  return (previewChartItem as { id?: string }).id === id ? (previewChartItem as CapsuleItem) : undefined;
}

/** The capsule items belonging to a condition, with selection merged in */
export function findCapsuleChildren(conditionId: string | number): CapsuleItem[] {
  return getCapsuleItems().filter((item) => item.isChildOf === conditionId);
}

/** The capsule items plus the preview chart item, mirroring the flux `itemsAndPreview` getter */
export function getCapsuleItemsAndPreview(): (CapsuleItem | PreviewChartItem)[] {
  return [...getCapsuleItems(), sqCapsuleRuntimeStore.getState().previewChartItem];
}

/**
 * Determines if there are non-zero length capsules to be displayed in the display range
 */
export function hasDisplayedCapsules(): boolean {
  return _.some(
    sqCapsuleRuntimeStore.getState().items,
    (capsule) =>
      (capsule.startTime as number) < sqDurationStore.getState().displayRange.end &&
      ((capsule.endTime as number) - (capsule.startTime as number) > 0 || _.isNil(capsule.endTime)),
  );
}

export function hasAggregatedCapsules(): boolean {
  return sqCapsuleRuntimeStore.getState().chartItems.filter((chartItem) => !!chartItem.isAggregated).length > 0;
}

export function hasSelectedAggregatedCapsules(): boolean {
  return (
    sqCapsuleRuntimeStore.getState().chartItems.filter((chartItem) => !!chartItem.isAggregated && chartItem.selected)
      .length > 0
  );
}

export function hasSelectedNoChartCapsules(): boolean {
  return !sqCapsuleRuntimeStore.getState().chartItems.some((chartItem) => chartItem.selected);
}

export function shouldDisplayCapsulesWarning(): boolean {
  const isDimmingEnabled = sqTrendStore.hideUnselectedItems;

  return (
    (!isDimmingEnabled && hasAggregatedCapsules()) ||
    (isDimmingEnabled && (hasSelectedAggregatedCapsules() || (hasSelectedNoChartCapsules() && hasAggregatedCapsules())))
  );
}

/**
 * Gets all of the values for the given capsule property, as strings
 */
export function getCapsuleValuesForProperty(capsulePropertyName: string): string[] {
  return _.chain(sqCapsuleRuntimeStore.getState().items)
    .map((capsule) => capsule.properties?.[capsulePropertyName] ?? undefined)
    .compact()
    .uniq()
    .sortBy()
    .value() as string[];
}

/**
 * Finds the capsule that encompasses the time passed in, suitable for selecting or unselecting.
 */
export function findChartCapsule(id: string, time: number): SelectableCapsule | undefined {
  const ZERO_LENGTH_TOLERANCE = 5;
  const zeroLengthLookupTime = nanosToMillis(time);

  const chartItem = _.find(sqCapsuleRuntimeStore.getState().chartItems, ['id', getCertainId(id)]);
  const capsule = _.find(chartItem?.capsules, (capsule) => {
    if (capsule.startTime === capsule.endTime) {
      return (
        zeroLengthLookupTime >= nanosToMillis(capsule.startTime) - ZERO_LENGTH_TOLERANCE &&
        zeroLengthLookupTime <= nanosToMillis(capsule.startTime) + ZERO_LENGTH_TOLERANCE
      );
    }
    return time >= capsule.startTime && time <= capsule.endTime;
  });

  if (capsule) {
    const selected =
      sqTrendCapsuleStore.isSelected(capsule.id) ||
      (capsule.isUncertain &&
        _.find(
          sqTrendCapsuleStore.selectedCapsules,
          (selectedCapsule) =>
            splitUniqueId(capsule.id).conditionId === splitUniqueId(selectedCapsule.id).conditionId &&
            (capsule.startTime === selectedCapsule.startTime || capsule.endTime === selectedCapsule.endTime),
        ));

    return _.assign({}, capsule, {
      itemType: ITEM_TYPES.CAPSULE,
      selected,
    }) as SelectableCapsule;
  }
}
